import { createSelector, createSelectorCreator, defaultMemoize } from 'reselect'
import getCurrentCamera from 'helpers/cameras/getCurrentCamera'
import computeScores from 'helpers/cameras/computeRecentScore'
import computeFriendlyTilt from 'helpers/cameras/computeFriendlyTilt'
import { RECENT_DISPLAY_COUNT, ACCESS_PERIPHERAL_TYPES, ACCESS_USAGE_MAP, ACCESS_REVERSE_MAP } from 'helpers/globals'
import getWireDistance from 'helpers/wires/getWireDistance'
import convertUnitToMap from 'helpers/convertUnitToMap'
import { modalConfigurations } from 'components/Modal/configurations'
import prop from 'ramda/src/prop'
import equals from 'ramda/src/equals'
import defaultTo from 'ramda/src/defaultTo'
import flatten from 'ramda/src/flatten'
import filter from 'ramda/src/filter'

const createDeepEqualSelector = createSelectorCreator(
  defaultMemoize,
  equals
)

export const cameraPropSelector = key =>
  createSelector(
    state => getCurrentCamera(state.cameras),
    prop(key)
  )

export const uiPropSelector = key =>
  createSelector(
    prop('ui'),
    prop(key)
  )

export const presentationPropSelector = key =>
  createSelector(
    prop('presentationValues'),
    pValues => pValues[key].value
  )

export const friendlyTiltSelector =
  createSelector(
    cameraPropSelector('tilt'),
    cameraPropSelector('horizontalAngleOfView'),
    cameraPropSelector('verticalAngleOfView'),
    cameraPropSelector('fovMode'),
    (tilt, horizontalAngleOfView, verticalAngleOfView, fovMode) => computeFriendlyTilt({ tilt, horizontalAngleOfView, verticalAngleOfView, fovMode, raw: true })
  )

export const friendlyTiltPresentationSelector =
  createSelector(
    cameraPropSelector('tilt'),
    cameraPropSelector('horizontalAngleOfView'),
    cameraPropSelector('verticalAngleOfView'),
    cameraPropSelector('fovMode'),
    (tilt, horizontalAngleOfView, verticalAngleOfView, fovMode) => computeFriendlyTilt({ tilt, horizontalAngleOfView, verticalAngleOfView, fovMode })
  )

export const mapPropSelector = key =>
  createSelector(
    prop('map'),
    prop(key)
  )

export const cameraSelector = id =>
  createSelector(
    prop('cameras'),
    cameras => cameras.find(c => c.id === id)
  )

export const camerasSelector = state => state.cameras

export const cameraCountSelector =
  createSelector(
    camerasSelector,
    cameras => cameras.filter(c => !c.imagerId).length
  )

export const mapSelector = state => state.map

export const markupsSelector =
  createSelector(
    mapSelector,
    prop('markups')
  )

export const labelsSelector =
  createSelector(
    markupsSelector,
    markups => markups.filter(m => m.type === 'label')
  )

export const wallsSelector =
  createSelector(
    markupsSelector,
    markups => markups.filter(m => m.type === 'wall')
  )

const _wallsLocationsSelector =
  createSelector(
    wallsSelector,
    walls => walls.map(w => ({ overlay: w.overlay, location: w.location }))
  )

export const wallsLocationsSelector =
  createDeepEqualSelector(
    _wallsLocationsSelector,
    res => res
  )

export const stateRecentModelsSelector = state => state.recentModels
export const stateRecentDoorControllerModelsSelector = state => state.recentDoorControllerModels
export const projectsSelector = state => state.projects

export const recentModelsSelector =
  createSelector(
    stateRecentModelsSelector,
    projectsSelector,
    (recentModels, projects) => computeScores({ recentModels, projects }).sort((a, b) => b.score - a.score).slice(0, RECENT_DISPLAY_COUNT).map(m => m.id)
  )

export const recentDoorControllerModelsSelector =
  createSelector(
    stateRecentDoorControllerModelsSelector,
    projectsSelector,
    (recentModels, projects) => computeScores({ recentModels, projects }).sort((a, b) => b.score - a.score).slice(0, RECENT_DISPLAY_COUNT).map(m => m.id)
  )

export const stateFavoriteModelsSelector = state => state.favoriteModels
export const stateFavoriteDoorControllerModelsSelector = state => state.favoriteDoorControllerModels
export const controllerModelSelector = (_, props) => props.controllerModel

export const favoriteModelsSelector =
  createSelector(
    stateFavoriteModelsSelector,
    stateFavoriteDoorControllerModelsSelector,
    controllerModelSelector,
    (favoriteModels, favoriteDoorControllerModels, controllerModel) => (controllerModel ? favoriteDoorControllerModels : favoriteModels) || []
  )

export const legacyProjectsSelector = state => state.legacyProjects

const defaultToObject = defaultTo({})

export const projectListSelector =
  createSelector(
    legacyProjectsSelector,
    projectsSelector,
    (legacyProjects, projects) => [ ...defaultToObject(legacyProjects), ...projects.filter(p => !p.permalink) ]
  )

export const activeCameraSelector =
  createSelector(
    camerasSelector,
    cameras => cameras.find(c => c.active === true)
  )

export const lensImagerModelSelector =
  createSelector(
    camerasSelector,
    activeCameraSelector,
    (cameras, lens) => cameras.find(c => c.id === lens.imagerId).model
  )

export const cameraModelsSelector = state => state.cameraModels

export const lensModelsSelector =
  createSelector(
    cameraModelsSelector,
    lensImagerModelSelector,
    activeCameraSelector,
    (cameraModels, model, lens) => {
      const options = /PTZ/.test(model.multi_imager_type) && lens.isPtzImager ? model.ptz_imager_option_ids : model.imager_option_ids
      return cameraModels.filter(m => options.includes(m.id))
    }
  )

export const userSettingsPropSelector = key =>
  createSelector(
    prop('userSettings'),
    prop(key)
  )

export const networkClosetsSelector = state => state.entities.networkClosets.byId
export const idSelector = (_, props) => props.id
export const makeNetworkClosetSelector = _ =>
  createSelector(
    networkClosetsSelector,
    idSelector,
    (networkClosets, id) => networkClosets[id]
  )

export const makeNetworkClosetPropSelector = key =>
  createSelector(
    makeNetworkClosetSelector(),
    prop(key)
  )

export const currentEntitySelector = state => state.currentEntity
export const currentEntityIdSelector = state => state.currentEntity.id

export const makeNetworkClosetSelectedSelector = _ =>
  createSelector(
    currentEntitySelector,
    idSelector,
    (currentEntity, id) => currentEntity.type === 'networkCloset' && currentEntity.id === id
  )

export const wiresSelector = state => state.entities.wires.byId
export const wiresArraySelector =
  createSelector(
    wiresSelector,
    wires => Object.values(wires)
  )

export const wiredDevicesSelector = state => state.entities.wiredDevices.byId
export const wiredDeviceIdsSelector = state => state.entities.wiredDevices.allIds

export const doorControllersSelector = state => state.entities.doorControllers.byId
export const doorControllerIdsSelector = state => state.entities.doorControllers.allIds
export const doorsSelector = state => state.entities.doors.byId
export const doorIdsSelector = state => state.entities.doors.allIds

export const wiredDevicesArraySelector =
  createSelector(
    wiredDevicesSelector,
    wiredDevices => Object.values(wiredDevices)
  )

export const endingWiresSelector =
  createSelector(
    currentEntitySelector,
    wiresSelector,
    (currentEntity, wires) => Object.values(wires).filter(wire => equals(wire.end, currentEntity))
  )

export const currentEntityWireSelector =
  createSelector(
    currentEntitySelector,
    wiresSelector,
    (currentEntity, wires) => Object.values(wires).find(w => equals(w.start, currentEntity) && w.end.type === 'networkCloset')
  )

export const connectedNetworkClosetSelector =
  createSelector(
    currentEntityWireSelector,
    networkClosetsSelector,
    (wire, networkCloset) => wire && networkCloset[wire.end.id]
  )

export const networkClosetIdsSelector = state => state.entities.networkClosets.allIds
export const networkClosetExistsSelector =
  createSelector(
    networkClosetIdsSelector,
    ids => ids.length > 0
  )

export const networkClosetsArraySelector =
  createSelector(
    networkClosetsSelector,
    networkClosets => Object.values(networkClosets)
  )

export const currentNetworkClosetSelector =
  createSelector(
    networkClosetsSelector,
    currentEntityIdSelector,
    (networkClosets, id) => networkClosets[id]
  )

export const currentWiredDeviceSelector =
  createSelector(
    wiredDevicesSelector,
    currentEntityIdSelector,
    (wiredDevices, id) => wiredDevices[id]
  )

export const wiredDeviceTypeSelector =
  createSelector(
    currentEntitySelector,
    wiredDevicesSelector,
    ({ id, type }, wiredDevices) => type === 'wiredDevice' && wiredDevices[id].type
  )

export const selectableCamerasSelector =
  createSelector(
    camerasSelector,
    cameras => cameras.filter(c => !c.imagerId)
  )

export const connectableDevicesSelector =
  createSelector(
    selectableCamerasSelector,
    doorControllersSelector,
    doorsSelector,
    wiredDevicesSelector,
    wiresArraySelector,
    networkClosetsSelector,
    (cameras, doorControllers, doors, wiredDevices, wires, networkClosets) => {
      const validDevices = []
      const invalidDevices = []
      const cameraIdMap = new Map()
      const doorControllerMap = new Map()
      const wiredDeviceMap = new Map()

      wires.forEach(wire => {
        switch (wire.start.type) {
          case 'camera':
            return cameraIdMap.set(wire.start.id, wire.end.id)
          case 'doorController':
            return wire.end.type !== 'doorController' && doorControllerMap.set(wire.start.id, wire.end.id)
          case 'wiredDevice':
            return wiredDeviceMap.set(wire.start.id, wire.end.id)
        }
      })

      cameras.forEach(camera => {
        const endId = cameraIdMap.get(camera.id)
        const result = Object.assign({}, camera, { type: 'camera' }, endId && { networkClosetName: networkClosets[endId].name })

        endId ? invalidDevices.push(result) : validDevices.push(result)
      })

      Object.values(doorControllers).forEach(doorController => {
        if (doorController.isExpansion && doorController.model.connection !== 'Ethernet') {
          return invalidDevices.push(Object.assign({}, doorController, { type: 'nonEthernet' }))
        }

        const endId = doorControllerMap.get(doorController.id)
        const result = Object.assign({}, doorController, { type: 'doorController' }, endId && { networkClosetName: networkClosets[endId].name })

        endId ? invalidDevices.push(result) : validDevices.push(result)
      })

      Object.values(wiredDevices).forEach(wiredDevice => {
        const endId = wiredDeviceMap.get(wiredDevice.id)
        const result = Object.assign({}, wiredDevice, { type: 'wiredDevice' }, endId && { networkClosetName: networkClosets[endId].name })

        endId ? invalidDevices.push(result) : validDevices.push(result)
      })

      Object.values(doors).forEach(door => invalidDevices.push(Object.assign({}, door, { type: 'nonEthernet' })))

      return { validDevices, invalidDevices }
    }
  )

const isImperialSelector = uiPropSelector('isImperial')

export const dropLengthInputSelector =
  createSelector(
    currentEntitySelector,
    doorControllersSelector,
    networkClosetsSelector,
    ({ id, type }, doorControllers, networkClosets) => {
      if (type === 'doorController') return doorControllers[id] && doorControllers[id].dropLength
      else return networkClosets[id] && networkClosets[id].dropLength
    }
  )

export const convertedDropLengthSelector =
  createSelector(
    dropLengthInputSelector,
    isImperialSelector,
    (dropLength, isImperial) => dropLength !== null && (isImperial ? dropLength : Math.round(convertUnitToMap({ value: dropLength, isImperial: true })))
  )

export const connectedDevicesSelector =
  createSelector(
    endingWiresSelector,
    camerasSelector,
    doorControllersSelector,
    wiredDevicesSelector,
    dropLengthInputSelector,
    isImperialSelector,
    (wires, cameras, doorControllers, wiredDevices, dropLength, isImperial) => {
      const connectedDevices = wires.map(wire => {
        let device
        if (wire.start.type === 'camera') device = cameras.find(c => c.id === wire.start.id)
        else if (wire.start.type === 'doorController') device = doorControllers[wire.start.id]
        else device = wiredDevices[wire.start.id]

        return {
          ...device,
          ...getWireDistance(wire.overlay, isImperial, dropLength),
          objType: wire.start.type,
          wire
        }
      })

      return {
        connectedDevices,
        totalDistance: connectedDevices.reduce((total, current) => total + current.rawDistance, 0).toFixed() + (isImperial ? ' ft' : ' m')
      }
    }
  )

export const exportCurrentEntityWireSelector =
  createSelector(
    currentEntitySelector,
    wiresArraySelector,
    camerasSelector,
    (currentEntity, wires, cameras) => {
      if (currentEntity.type === 'camera') {
        const camera = getCurrentCamera(cameras)
        return wires.find(w => w.start.type === 'camera' && w.start.id === (camera.imagerId || camera.id))
      }

      return wires.find(w => equals(w.start, currentEntity))
    }
  )

export const exportWireEndDeviceSelector =
  createSelector(
    exportCurrentEntityWireSelector,
    networkClosetsSelector,
    doorControllersSelector,
    (wire, networkCloset, doorControllers) => wire && (wire.end.type === 'networkCloset' ? networkCloset[wire.end.id] : doorControllers[wire.end.id])
  )

export const exportWireDistanceSelector =
  createSelector(
    exportCurrentEntityWireSelector,
    isImperialSelector,
    exportWireEndDeviceSelector,
    (wire, isImperial, endDevice) => wire && getWireDistance(wire.overlay, isImperial, endDevice.dropLength)
  )

export const makeWiredDeviceSelector = _ =>
  createSelector(
    wiredDevicesSelector,
    idSelector,
    (wiredDevices, id) => wiredDevices[id]
  )

export const makeWiredDevicePropSelector = key =>
  createSelector(
    makeWiredDeviceSelector(),
    prop(key)
  )

export const makeWiredDeviceSelectedSelector = _ =>
  createSelector(
    currentEntitySelector,
    idSelector,
    (currentEntity, id) => currentEntity.type === 'wiredDevice' && currentEntity.id === id
  )

export const makeWireStartDeviceSelector = _ =>
  createSelector(
    (_, props) => props.start,
    camerasSelector,
    doorControllersSelector,
    doorsSelector,
    wiredDevicesSelector,
    (start, cameras, doorControllers, doors, wiredDevices) => {
      switch (start.type) {
        case 'camera':
          return cameras.find(c => c.id === start.id)
        case 'doorController':
          return doorControllers[start.id]
        case 'door':
          return doors[start.id]
        case 'wiredDevice':
          return wiredDevices[start.id]
      }
    }
  )

export const makeWireEndDeviceSelector = _ =>
  createSelector(
    (_, props) => props.end,
    networkClosetsSelector,
    doorControllersSelector,
    (end, networkClosets, doorControllers) => end.type === 'networkCloset' ? networkClosets[end.id] : doorControllers[end.id]
  )

export const makeWireSelectedSelector = _ =>
  createSelector(
    currentEntitySelector,
    idSelector,
    (currentEntity, id) => currentEntity.type === 'wire' && currentEntity.id === id
  )

export const makeWireStartDeviceSelectedSelector = _ =>
  createSelector(
    currentEntitySelector,
    (_, props) => props.start,
    (currentEntity, start) => equals(currentEntity, start)
  )

export const makeWireEndDeviceSelectedSelector = _ =>
  createSelector(
    currentEntitySelector,
    (_, props) => props.end,
    (currentEntity, end) => equals(currentEntity, end)
  )

export const makeExpansionControllerSharesParentNetworkClosetSelector = _ =>
  createSelector(
    (_, props) => props.start.type,
    makeWireStartDeviceSelector(),
    (_, props) => props.end.id,
    wiresSelector,
    currentEntitySelector,
    (startType, startDevice, closetId, wires, currentEntity) => {
      if (startType !== 'doorController') return false

      if (startDevice.isExpansion) {
        return Object.values(wires).some(wire => equals(wire.start, { id: startDevice.parentId, type: 'doorController' }) && equals(wire.end, { id: closetId, type: 'networkCloset' }))
      }

      return startDevice.expansionIds.some(expansionId => {
        if (!equals(currentEntity, { id: expansionId, type: 'doorController' })) return false

        return Object.values(wires).some(wire =>
          equals(wire.start, { id: expansionId, type: 'doorController' }) && equals(wire.end, { id: closetId, type: 'networkCloset' })
        )
      })
    }
  )

export const makeShowExpansionControllerWireSelector = _ =>
  createSelector(
    (_, props) => props.start.type,
    makeWireStartDeviceSelector(),
    makeExpansionControllerSharesParentNetworkClosetSelector(),
    currentEntitySelector,
    (startType, startDevice, sharedClosetExists, currentEntity) => {
      if (startType !== 'doorController' || currentEntity.type !== 'doorController' || !sharedClosetExists) return false

      if (startDevice.isExpansion) return currentEntity.id === startDevice.parentId

      return true
    }
  )

export const makeShowWireSelector = _ =>
  createSelector(
    makeWireSelectedSelector(),
    makeWireStartDeviceSelectedSelector(),
    makeWireEndDeviceSelectedSelector(),
    makeShowExpansionControllerWireSelector(),
    (wireSelected, startDeviceSelected, endDeviceSelected, associatedControllerSelected) => wireSelected || startDeviceSelected || endDeviceSelected || associatedControllerSelected
  )

export const currentWireSelector =
  createSelector(
    wiresSelector,
    currentEntityIdSelector,
    (wires, id) => wires[id]
  )

export const currentWireStartSelector =
  createSelector(
    currentWireSelector,
    prop('start')
  )

export const wireStartDeviceSelector =
  createSelector(
    currentWireStartSelector,
    camerasSelector,
    doorControllersSelector,
    doorsSelector,
    wiredDevicesSelector,
    (start, cameras, doorControllers, doors, wiredDevices) => {
      switch (start.type) {
        case 'camera':
          return cameras.find(c => c.id === start.id)
        case 'doorController':
          return doorControllers[start.id]
        case 'door':
          return doors[start.id]
        case 'wiredDevice':
          return wiredDevices[start.id]
      }
    }
  )

export const currentWireEndSelector =
  createSelector(
    currentWireSelector,
    prop('end')
  )

export const wireEndDeviceSelector =
  createSelector(
    currentWireEndSelector,
    networkClosetsSelector,
    doorControllersSelector,
    (end, networkClosets, doorControllers) => end.type === 'networkCloset' ? networkClosets[end.id] : doorControllers[end.id]
  )

export const currentDeviceSelector =
  createSelector(
    currentEntitySelector,
    camerasSelector,
    doorControllersSelector,
    doorsSelector,
    networkClosetsSelector,
    wiredDevicesSelector,
    ({ id, type }, cameras, doorControllers, doors, networkClosets, wiredDevices) => {
      switch (type) {
        case 'camera':
          return cameras.find(c => c.id === id)
        case 'doorController':
          return doorControllers[id]
        case 'door':
          return doors[id]
        case 'networkCloset':
          return networkClosets[id]
        case 'wiredDevice':
          return wiredDevices[id]
      }
    }
  )

export const makeDeviceWireSelector = _ =>
  createSelector(
    idSelector,
    (_, props) => props.objType,
    wiresSelector,
    (id, objType, wires) => Object.values(wires).find(w => w.start.id === id && w.start.type === objType)
  )

export const makeDeviceWireSelectedSelector = _ =>
  createSelector(
    makeDeviceWireSelector(),
    currentEntitySelector,
    (wire, currentEntity) => wire && currentEntity.type === 'wire' && currentEntity.id === wire.id
  )

export const deviceCountSelector =
  createSelector(
    cameraCountSelector,
    doorControllerIdsSelector,
    doorIdsSelector,
    networkClosetIdsSelector,
    wiredDeviceIdsSelector,
    (cameraCount, doorControllerIds, doorIds, networkClosetIds, wiredDeviceIds) => cameraCount + doorControllerIds.length + doorIds.length + networkClosetIds.length + wiredDeviceIds.length
  )

export const isControllerSelector = (_, props) => props.isController

export const makeDoorEntitySelector = _ =>
  createSelector(
    doorControllersSelector,
    doorsSelector,
    idSelector,
    isControllerSelector,
    (doorControllers, doors, id, isController) => isController ? doorControllers[id] : doors[id]
  )

export const makeDoorEntityPropSelector = key =>
  createSelector(
    makeDoorEntitySelector(),
    prop(key)
  )

export const makeDoorEntitySelectedSelector = _ =>
  createSelector(
    currentEntitySelector,
    idSelector,
    isControllerSelector,
    (currentEntity, id, isController) => currentEntity.id === id && currentEntity.type === (isController ? 'doorController' : 'door')
  )

export const currentDoorControllerSelector =
  createSelector(
    currentEntitySelector,
    doorControllersSelector,
    doorsSelector,
    ({ type, id }, doorControllers, doors) => doorControllers[type === 'doorController' ? id : doors[id].controllerId]
  )

export const doorControllerDoorIdsSelector =
  createSelector(
    currentDoorControllerSelector,
    prop('doorIds')
  )

export const doorControllerExpansionIdsSelector =
  createSelector(
    currentDoorControllerSelector,
    prop('expansionIds')
  )

export const doorControllerIsExpansionSelector =
  createSelector(
    currentDoorControllerSelector,
    prop('isExpansion')
  )

export const doorControllerParentIdSelector =
  createSelector(
    currentDoorControllerSelector,
    prop('parentId')
  )

export const doorControllerAssociatedIdsSelector =
  createSelector(
    currentEntityIdSelector,
    doorControllerIsExpansionSelector,
    doorControllerExpansionIdsSelector,
    doorControllerParentIdSelector,
    doorControllersSelector,
    (id, isExpansion, expansionIds, parentId, doorControllers) => {
      if (isExpansion) return doorControllers[parentId].expansionIds.filter(expansionId => expansionId !== id).concat(parentId)

      return expansionIds
    }
  )

export const currentDoorControllerModelSelector =
  createSelector(
    currentDoorControllerSelector,
    prop('model')
  )

export const doorControllerUsagesSelector =
  createSelector(
    doorControllerDoorIdsSelector,
    doorsSelector,
    (doorIds, doors) => {
      const usages = { doors: doorIds.length }
      Object.keys(ACCESS_REVERSE_MAP).forEach(limitType => usages[limitType] = 0)

      doorIds.forEach(doorId =>
        ACCESS_PERIPHERAL_TYPES.forEach(peripheralType => usages[ACCESS_USAGE_MAP[peripheralType]] += doors[doorId][peripheralType].length)
      )

      return usages
    }
  )

export const doorEntityTotalsSelector =
  createSelector(
    doorControllersSelector,
    doorsSelector,
    currentEntityIdSelector,
    isControllerSelector,
    (doorControllers, doors, id, isController) => {
      const doorIds = isController ? doorControllers[id].doorIds : [id]

      const totals = isController ? { doors: doorIds.length } : {}
      ACCESS_PERIPHERAL_TYPES.forEach(peripheralType => totals[peripheralType] = 0)

      doorIds.forEach(doorId =>
        ACCESS_PERIPHERAL_TYPES.forEach(peripheralType => totals[peripheralType] += doors[doorId][peripheralType].length)
      )

      return filter(Boolean, totals)
    }
  )

export const showDoorControllerTotalUsagesSelector =
  createSelector(
    doorControllerAssociatedIdsSelector,
    associatedIds => associatedIds.length > 0
  )

export const doorControllerTotalUsagesSelector =
  createSelector(
    doorControllerUsagesSelector,
    doorControllerAssociatedIdsSelector,
    doorControllersSelector,
    doorsSelector,
    (usages, associatedIds, doorControllers, doors) => {
      if (associatedIds.length === 0) return { ...usages }

      const totalUsages = { ...usages }

      associatedIds.forEach(controllerId => {
        const controller = doorControllers[controllerId]

        totalUsages.doors += controller.doorIds.length

        controller.doorIds.forEach(doorId =>
          ACCESS_PERIPHERAL_TYPES.forEach(peripheralType => totalUsages[ACCESS_USAGE_MAP[peripheralType]] += doors[doorId][peripheralType].length)
        )
      })

     return totalUsages
   }
 )

export const showDoorControllerAggregateTotalsSelector =
  createSelector(
    doorControllerExpansionIdsSelector,
    expansionIds => expansionIds.length > 0
  )

export const doorControllerAggregateTotalsSelector =
  createSelector(
    currentEntityIdSelector,
    doorControllerExpansionIdsSelector,
    doorControllersSelector,
    doorsSelector,
    (id, expansionIds, doorControllers, doors) => {
      const totals = { doors: 0 }

      ACCESS_PERIPHERAL_TYPES.forEach(peripheralType => totals[peripheralType] = 0)

      expansionIds.concat(id).forEach(controllerId => {
        const controller = doorControllers[controllerId]

        totals.doors += controller.doorIds.length

        controller.doorIds.forEach(doorId =>
          ACCESS_PERIPHERAL_TYPES.forEach(peripheralType => totals[peripheralType] += doors[doorId][peripheralType].length)
        )
      })

      return filter(Boolean, totals)
    }
  )

export const connectedDoorWiresSelector =
  createSelector(
    currentEntitySelector,
    wiresSelector,
    (currentEntity, wires) => Object.values(wires).filter(wire => wire.start.type === 'door' && equals(wire.end, currentEntity))
  )

export const connectedDoorsSelector =
  createSelector(
    connectedDoorWiresSelector,
    doorsSelector,
    dropLengthInputSelector,
    isImperialSelector,
    (wires, doors, dropLength, isImperial) => {
      const connectedDoors = wires.map(wire => {
        const door = doors[wire.start.id]

        return {
          ...door,
          ...getWireDistance(wire.overlay, isImperial, dropLength)
        }
      })

      return {
        connectedDoors,
        totalDistance: connectedDoors.reduce((total, current) => total + current.rawDistance, 0).toFixed() + (isImperial ? ' ft' : ' m')
      }
    }
  )

export const connectedExpansionsSelector =
  createSelector(
    doorControllerExpansionIdsSelector,
    doorControllersSelector,
    doorsSelector,
    wiresSelector,
    networkClosetsSelector,
    isImperialSelector,
    (expansionIds, doorControllers, doors, wires, networkClosets, isImperial) => {
      const expansions = expansionIds.map(expansionId => {
        const controller = doorControllers[expansionId]

        const connectedDoors = controller.doorIds.map(doorId => {
          const door = doors[doorId]
          const wire = Object.values(wires).find(wire => equals(wire.start, { id: doorId, type: 'door' }) && equals(wire.end, { id: expansionId, type: 'doorController' }))

          return {
            ...door,
            ...getWireDistance(wire.overlay, isImperial, controller.dropLength)
          }
        })

        const wire = Object.values(wires).find(wire => equals(wire.start, { id: expansionId, type: 'doorController' }))

        return Object.assign({}, {
            ...controller,
            doors: connectedDoors,
            totalDistance: connectedDoors.reduce((total, current) => total + current.rawDistance, 0).toFixed() + (isImperial ? ' ft' : ' m')
          },
          wire && {
            ...getWireDistance(wire.overlay, isImperial, wire.end.type === 'doorController' ? doorControllers[wire.end.id].dropLength : networkClosets[wire.end.id].dropLength),
            hasWire: true,
            wireIsEthernet: controller.model.connection === 'Ethernet'
          }
        )
      })

      return {
        expansions,
        totalExpansionDistance: expansions.filter(controller => controller.hasWire && !controller.wireIsEthernet).reduce((total, current) => total + current.rawDistance, 0).toFixed() + (isImperial ? ' ft' : ' m')
      }
    }
  )

export const doorControllerModelsSelector = state => state.doorControllerModels
export const expansionControllerModelsSelector = state => state.expansionControllerModels

export const expansionControllerParentModelSelector =
  createSelector(
    currentDoorControllerModelSelector,
    doorControllerIsExpansionSelector,
    doorControllerParentIdSelector,
    doorControllersSelector,
    doorControllerModelsSelector,
    (model, isExpansion, parentId, doorControllers, doorControllerModels) => doorControllerModels.byId[isExpansion ? doorControllers[parentId].model.id : model.id]
  )

export const expansionControllerParentModelNameSelector =
  createSelector(
    expansionControllerParentModelSelector,
    prop('text')
  )

export const compatibleExpansionControllerModelsSelector =
  createSelector(
    expansionControllerParentModelSelector,
    expansionControllerModelsSelector,
    (parentModel, expansionModels) => {
      if (!parentModel || !parentModel.allows_expansion) return []

      return parentModel.expansion_option_ids.map(id => expansionModels.byId[id])
    }
  )

export const doorControllerModelCountSelector =
  createSelector(
    doorControllerIsExpansionSelector,
    doorControllerModelsSelector,
    compatibleExpansionControllerModelsSelector,
    (isExpansion, doorControllerModels, expansionControllerModels) => isExpansion ? expansionControllerModels.length : doorControllerModels.allIds.length
  )

export const doorControllerModelTotalLimitsSelector =
  createSelector(
    expansionControllerParentModelSelector,
    parentModel => parentModel ? parentModel.total_limits : null
  )

export const doorControllerShowAddFirstDoorSelector =
  createSelector(
    doorControllerDoorIdsSelector,
    doorControllerIsExpansionSelector,
    doorControllerAssociatedIdsSelector,
    doorControllersSelector,
    doorControllerModelTotalLimitsSelector,
    (doorIds, isExpansion, associatedIds, doorControllers, totalLimits) => {
      if (!isExpansion && associatedIds.length === 0) return doorIds.length === 0

      if (doorIds.length) return false

      return associatedIds.reduce((total, controllerId) => total + doorControllers[controllerId].doorIds.length, 0) < totalLimits.doors
    }
  )

export const expansionControllerExportParentSelector =
  createSelector(
    doorControllerIsExpansionSelector,
    doorControllerParentIdSelector,
    doorControllersSelector,
    (isExpansion, parentId, doorControllers) => isExpansion ? doorControllers[parentId] : null
  )

export const currentDoorSelector =
  createSelector(
    doorsSelector,
    currentEntityIdSelector,
    (doors, id) => doors[id]
  )

export const doorDoorControllerIdSelector =
  createSelector(
    currentDoorSelector,
    prop('controllerId')
  )

export const doorDoorControllerSelector =
  createSelector(
    doorDoorControllerIdSelector,
    doorControllersSelector,
    (id, doorControllers) => doorControllers[id]
  )

export const doorPeripheralsSelector =
  createSelector(
    currentDoorSelector,
    state => state.entities,
    (door, entities) => flatten(ACCESS_PERIPHERAL_TYPES.map(type => door[type].map(id => ({ ...entities[type].byId[id], peripheralType: type }))))
  )

export const peripheralSelector =
  createSelector(
    state => state.entities,
    (_, props) => props.peripheralType,
    (_, props) => props.peripheralId,
    (entities, peripheralType, peripheralId) => entities[peripheralType].byId[peripheralId]
  )

const modalSelector = uiPropSelector('modal')
const offlineTimeLimit = uiPropSelector('offlineTimeLimit')

export const modalTitleSelector =
  createSelector(
    modalSelector,
    isImperialSelector,
    offlineTimeLimit,
    currentEntitySelector,
    wiredDevicesSelector,
    doorControllersSelector,
    doorsSelector,
    (modal, isImperial, offlineTimeLimit, { id, type }, wiredDevices, doorControllers, doors) => {
      switch (modal) {
        case 'AnalyticZonesSelector':
          return isImperial ? 'Edit Pixels Per Foot (PPF) Zones' : 'Edit Pixels Per Meter (PPM) Zones'
        case 'EditLocation':
          switch (type) {
            case 'camera':
              return 'Camera Location'
            case 'doorController':
              return 'Door Controller Location'
            case 'door':
              return 'Door Location'
            case 'networkCloset':
              return 'Network Closet Location'
            case 'wiredDevice':
              return `${wiredDevices[id].type} Location`
          }
        case 'DoorControllerDesigner':
          const doorController = doorControllers[type === 'doorController' ? id : doors[id].controllerId]
          return `Configure ${doorController.name} (${doorController.model.text})`
        case 'LoginForm':
          if (offlineTimeLimit) return 'Time Limit Reached'
        default:
          return (modalConfigurations[`${modal}Config`] || {}).title
      }
    }
  )



// WEBPACK FOOTER //
// ./src/selectors/components.js